import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiSearch,
  FiCopy,
  FiCheck,
  FiChevronDown,
  FiMapPin,
  FiMap,
  FiHash,
  FiNavigation,
  FiList,
  FiHome,
} from 'react-icons/fi';
import sl from 'sl-address';

const TABS = [
  { id: 'postal', label: 'Postal Lookup', icon: FiHash },
  { id: 'search', label: 'Fuzzy Search', icon: FiSearch },
  { id: 'autocomplete', label: 'Auto-complete', icon: FiNavigation },
  { id: 'explorer', label: 'Explorer', icon: FiMap },
  { id: 'normalize', label: 'Normalize', icon: FiMapPin },
  { id: 'citylookup', label: 'City Lookup', icon: FiHome },
  { id: 'postals', label: 'Postals by District', icon: FiList },
];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 bg-white/10 hover:bg-white/15 text-slate-400 hover:text-slate-200"
      title="Copy to clipboard"
    >
      {copied ? (
        <>
          <FiCheck className="w-3.5 h-3.5 text-accent-400" />
          <span className="text-accent-400">Copied!</span>
        </>
      ) : (
        <>
          <FiCopy className="w-3.5 h-3.5" />
          <span>Copy</span>
        </>
      )}
    </button>
  );
}

function Toast({ message, visible }) {
  if (!visible) return null;
  return (
    <div className="fixed bottom-6 right-6 z-50 toast">
      <div className="bg-primary-600 text-white px-5 py-3 rounded-xl shadow-xl text-sm font-medium flex items-center gap-2">
        <FiCheck className="w-4 h-4" />
        {message}
      </div>
    </div>
  );
}

function QuickExamples({ examples, onSelect }) {
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      <span className="text-xs text-slate-500 font-medium self-center">Try:</span>
      {examples.map((ex) => (
        <button
          key={ex}
          onClick={() => onSelect(ex)}
          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-500/10 text-primary-400 hover:bg-primary-500/20 transition-colors duration-200 border border-primary-500/20"
        >
          {ex}
        </button>
      ))}
    </div>
  );
}

/* ───────────────────────── Tab A: Postal Lookup ───────────────────────── */
function PostalLookup() {
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [readable, setReadable] = useState('');
  const [isValid, setIsValid] = useState(null);
  const [searched, setSearched] = useState(false);

  const lookup = useCallback((val) => {
    setCode(val);
    if (!val.trim()) {
      setResult(null);
      setReadable('');
      setIsValid(null);
      setSearched(false);
      return;
    }
    setSearched(true);
    const info = sl.getInfoByPostal(val.trim());
    setResult(info);
    try {
      setReadable(sl.toReadable(val.trim()));
    } catch {
      setReadable('');
    }
    try {
      setIsValid(sl.isValidPostal(val.trim()));
    } catch {
      setIsValid(null);
    }
  }, []);

  return (
    <div className="space-y-5">
      <div className="relative">
        <FiHash className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5" />
        <input
          type="text"
          value={code}
          onChange={(e) => lookup(e.target.value)}
          placeholder="Enter postal code e.g. 10280"
          className="w-full rounded-xl border border-white/[0.1] focus:border-primary-500 focus:ring-0 outline-none p-4 pl-12 font-medium text-white bg-white/[0.05] transition-colors duration-200 placeholder:text-slate-500"
        />
      </div>

      <QuickExamples
        examples={['10280', '20000', '80000', '60000','80360']}
        onSelect={lookup}
      />

      {searched && result && (
        <div className="bg-white/[0.04] rounded-xl border border-white/[0.08] p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-white text-lg">Result</h4>
            <CopyButton
              text={`${result.city}, ${result.district}, ${result.province} - ${result.postalCode}`}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/[0.04] rounded-lg p-4">
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">
                City
              </p>
              <p className="font-semibold text-white">{result.city}</p>
            </div>
            <div className="bg-white/[0.04] rounded-lg p-4">
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">
                District
              </p>
              <p className="font-semibold text-white">{result.district}</p>
            </div>
            <div className="bg-white/[0.04] rounded-lg p-4">
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">
                Province
              </p>
              <p className="font-semibold text-white">{result.province}</p>
            </div>
            <div className="bg-white/[0.04] rounded-lg p-4">
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">
                Postal Code
              </p>
              <p className="font-semibold text-primary-400">{result.postalCode}</p>
            </div>
          </div>

          {readable && (
            <div className="bg-primary-500/10 rounded-lg p-4 border border-primary-500/20">
              <p className="text-xs text-primary-400 font-medium uppercase tracking-wider mb-1">
                Readable Format
              </p>
              <p className="font-medium text-primary-300">{readable}</p>
            </div>
          )}

          {isValid !== null && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-400">Also try:</span>
              <code className="font-mono bg-white/10 px-2 py-1 rounded text-xs text-slate-300">
                isValidPostal("{code}")
              </code>
              <span className="text-slate-500">=</span>
              <span
                className={`font-semibold ${
                  isValid ? 'text-accent-400' : 'text-red-400'
                }`}
              >
                {String(isValid)}
              </span>
            </div>
          )}
        </div>
      )}

      {searched && !result && (
        <div className="bg-white/[0.04] rounded-xl border border-white/[0.08] p-6 text-center">
          <div className="text-slate-500 mb-2">
            <FiSearch className="w-10 h-10 mx-auto" />
          </div>
          <p className="text-slate-400 font-medium">No results found</p>
          <p className="text-slate-500 text-sm mt-1">
            Try a valid Sri Lankan postal code
          </p>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Tab B: Fuzzy Search ───────────────────────── */
function FuzzySearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const timerRef = useRef(null);

  const doSearch = useCallback((q) => {
    if (!q.trim()) {
      setResults([]);
      setSearched(false);
      return;
    }
    setSearched(true);
    try {
      const res = sl.searchCity(q.trim(), 8);
      setResults(res || []);
    } catch {
      setResults([]);
    }
  }, []);

  const handleChange = useCallback(
    (val) => {
      setQuery(val);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => doSearch(val), 300);
    },
    [doSearch]
  );

  const handleQuick = useCallback(
    (val) => {
      setQuery(val);
      if (timerRef.current) clearTimeout(timerRef.current);
      doSearch(val);
    },
    [doSearch]
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const scoreColor = (score) => {
    if (score > 0.8) return 'bg-accent-500';
    if (score > 0.5) return 'bg-yellow-400';
    return 'bg-red-400';
  };

  const scoreTextColor = (score) => {
    if (score > 0.8) return 'text-accent-400';
    if (score > 0.5) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="space-y-5">
      <div className="relative">
        <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5" />
        <input
          type="text"
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="Search city e.g. maha ragama"
          className="w-full rounded-xl border border-white/[0.1] focus:border-primary-500 focus:ring-0 outline-none p-4 pl-12 font-medium text-white bg-white/[0.05] transition-colors duration-200 placeholder:text-slate-500"
        />
      </div>

      <QuickExamples
        examples={['maha ragama', 'galleo', 'colomb', 'kandy']}
        onSelect={handleQuick}
      />

      {searched && results.length > 0 && (
        <div className="space-y-3">
          {results.map((r, i) => (
            <div
              key={`${r.city}-${i}`}
              className="bg-white/[0.04] rounded-xl border border-white/[0.08] p-5 transition-all duration-200 hover:bg-white/[0.07]"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-white truncate">
                      {r.city}
                    </h4>
                    <CopyButton text={`${r.city}, ${r.district}, ${r.province}`} />
                  </div>
                  <p className="text-sm text-slate-400">
                    {r.district} &middot; {r.province}
                  </p>

                  {r.postalCodes && r.postalCodes.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {r.postalCodes.map((pc) => (
                        <span
                          key={pc}
                          className="inline-block px-2 py-0.5 text-xs font-mono font-medium rounded-md bg-primary-500/10 text-primary-400 border border-primary-500/20"
                        >
                          {pc}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="text-right flex-shrink-0 w-24">
                  <p
                    className={`text-sm font-bold ${scoreTextColor(r.score)}`}
                  >
                    {(r.score * 100).toFixed(0)}%
                  </p>
                  <div className="w-full h-2 bg-white/10 rounded-full mt-1 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${scoreColor(
                        r.score
                      )}`}
                      style={{ width: `${r.score * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {searched && results.length === 0 && (
        <div className="bg-white/[0.04] rounded-xl border border-white/[0.08] p-6 text-center">
          <div className="text-slate-500 mb-2">
            <FiSearch className="w-10 h-10 mx-auto" />
          </div>
          <p className="text-slate-400 font-medium">No results found</p>
          <p className="text-slate-500 text-sm mt-1">
            Try a different search term
          </p>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────── Tab C: Auto-complete ─────────────────────── */
function AutoComplete() {
  const [prefix, setPrefix] = useState('');
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);

  const doSearch = useCallback((val) => {
    setPrefix(val);
    if (!val.trim()) {
      setResults([]);
      setSearched(false);
      return;
    }
    setSearched(true);
    try {
      const res = sl.autocompleteCity(val.trim(), Infinity);
      setResults(res || []);
    } catch {
      setResults([]);
    }
  }, []);

  const highlightPrefix = (text, pref) => {
    const lowerText = text.toLowerCase();
    const lowerPref = pref.toLowerCase().trim();
    const idx = lowerText.indexOf(lowerPref);
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="font-bold text-primary-400">
          {text.slice(idx, idx + lowerPref.length)}
        </span>
        {text.slice(idx + lowerPref.length)}
      </>
    );
  };

  return (
    <div className="space-y-5">
      <div className="relative">
        <FiNavigation className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5" />
        <input
          type="text"
          value={prefix}
          onChange={(e) => doSearch(e.target.value)}
          placeholder="Type a prefix e.g. kan"
          className="w-full rounded-xl border border-white/[0.1] focus:border-primary-500 focus:ring-0 outline-none p-4 pl-12 font-medium text-white bg-white/[0.05] transition-colors duration-200 placeholder:text-slate-500"
        />
      </div>

      <QuickExamples
        examples={['kan', 'col', 'gal', 'mat']}
        onSelect={doSearch}
      />

      {searched && results.length > 0 && (
        <div className="bg-white/[0.04] rounded-xl border border-white/[0.08] overflow-hidden divide-y divide-white/[0.06]">
          {results.map((city, i) => {
            const cityName = typeof city === 'string' ? city : city.city || String(city);
            return (
              <div
                key={`${cityName}-${i}`}
                className="flex items-center justify-between px-5 py-3.5 hover:bg-white/[0.05] transition-colors duration-150 group"
              >
                <div className="flex items-center gap-3">
                  <FiMapPin className="w-4 h-4 text-slate-600 group-hover:text-primary-400 transition-colors" />
                  <span className="text-slate-200 font-medium">
                    {highlightPrefix(cityName, prefix)}
                  </span>
                </div>
                <CopyButton text={cityName} />
              </div>
            );
          })}
        </div>
      )}

      {searched && results.length === 0 && (
        <div className="bg-white/[0.04] rounded-xl border border-white/[0.08] p-6 text-center">
          <div className="text-slate-500 mb-2">
            <FiNavigation className="w-10 h-10 mx-auto" />
          </div>
          <p className="text-slate-400 font-medium">No suggestions found</p>
          <p className="text-slate-500 text-sm mt-1">
            Try a different prefix
          </p>
        </div>
      )}
    </div>
  );
}

/* ─────────────────── Tab D: Province/District/City Explorer ────────── */
function Explorer() {
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedCity, setSelectedCity] = useState(null);

  useEffect(() => {
    try {
      const p = sl.getProvinces();
      setProvinces(p || []);
    } catch {
      setProvinces([]);
    }
  }, []);

  const handleProvinceChange = useCallback((province) => {
    setSelectedProvince(province);
    setSelectedDistrict('');
    setCities([]);
    setSelectedCity(null);
    if (!province) {
      setDistricts([]);
      return;
    }
    try {
      const d = sl.getDistrictsByProvince(province);
      setDistricts(d || []);
    } catch {
      setDistricts([]);
    }
  }, []);

  const handleDistrictChange = useCallback((district) => {
    setSelectedDistrict(district);
    setSelectedCity(null);
    if (!district) {
      setCities([]);
      return;
    }
    try {
      const c = sl.getCitiesByDistrict(district);
      setCities(c || []);
    } catch {
      setCities([]);
    }
  }, []);

  const handleCityClick = useCallback((city) => {
    setSelectedCity((prev) => (prev === city ? null : city));
  }, []);

  return (
    <div className="space-y-5">
      {/* Province dropdown */}
      <div className="relative">
        <FiMap className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5 pointer-events-none" />
        <FiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5 pointer-events-none" />
        <select
          value={selectedProvince}
          onChange={(e) => handleProvinceChange(e.target.value)}
          className="w-full rounded-xl border border-white/[0.1] focus:border-primary-500 focus:ring-0 outline-none p-4 pl-12 pr-10 font-medium text-white appearance-none bg-white/[0.05] transition-colors duration-200 cursor-pointer"
        >
          <option value="" className="bg-slate-800">Select Province</option>
          {provinces.map((p) => (
            <option key={p} value={p} className="bg-slate-800">
              {p}
            </option>
          ))}
        </select>
      </div>

      {/* District dropdown */}
      {districts.length > 0 && (
        <div className="relative">
          <FiMapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5 pointer-events-none" />
          <FiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5 pointer-events-none" />
          <select
            value={selectedDistrict}
            onChange={(e) => handleDistrictChange(e.target.value)}
            className="w-full rounded-xl border border-white/[0.1] focus:border-primary-500 focus:ring-0 outline-none p-4 pl-12 pr-10 font-medium text-white appearance-none bg-white/[0.05] transition-colors duration-200 cursor-pointer"
          >
            <option value="" className="bg-slate-800">Select District</option>
            {districts.map((d) => (
              <option key={d} value={d} className="bg-slate-800">
                {d}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Cities grid */}
      {cities.length > 0 && (
        <div className="bg-white/[0.04] rounded-xl border border-white/[0.08] p-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-white">
              Cities in {selectedDistrict}
            </h4>
            <span className="text-xs font-medium text-slate-400 bg-white/10 px-2.5 py-1 rounded-full">
              {cities.length} cities
            </span>
          </div>
          <div className="max-h-72 overflow-y-auto">
            <div className="flex flex-wrap gap-2">
              {cities.map((city) => (
                <button
                  key={city}
                  onClick={() => handleCityClick(city)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 border ${
                    selectedCity === city
                      ? 'bg-primary-600 text-white border-primary-600 shadow-md'
                      : 'bg-white/[0.05] text-slate-300 border-white/[0.1] hover:bg-primary-500/10 hover:text-primary-400 hover:border-primary-500/20'
                  }`}
                >
                  {city}
                </button>
              ))}
            </div>
          </div>

          {selectedCity && (
            <div className="mt-4 pt-4 border-t border-white/[0.08]">
              <div className="flex items-center gap-2 mb-2">
                <FiMapPin className="w-4 h-4 text-primary-400" />
                <span className="font-semibold text-white">
                  {selectedCity}
                </span>
                <CopyButton
                  text={`${selectedCity}, ${selectedDistrict}, ${selectedProvince}`}
                />
              </div>
              <p className="text-sm text-slate-400">
                {selectedDistrict} &middot; {selectedProvince}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Tab E: Normalization ───────────────────────── */
function Normalize() {
  const [input, setInput] = useState('');
  const [normalized, setNormalized] = useState('');
  const [hasResult, setHasResult] = useState(false);

  const handleInput = useCallback((val) => {
    setInput(val);
    if (!val.trim()) {
      setNormalized('');
      setHasResult(false);
      return;
    }
    try {
      const n = sl.normalize(val);
      setNormalized(n || '');
      setHasResult(true);
    } catch {
      setNormalized('');
      setHasResult(false);
    }
  }, []);

  return (
    <div className="space-y-5">
      <div className="relative">
        <FiMapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5" />
        <input
          type="text"
          value={input}
          onChange={(e) => handleInput(e.target.value)}
          placeholder="Enter messy city name e.g. MAHARAGAMA"
          className="w-full rounded-xl border border-white/[0.1] focus:border-primary-500 focus:ring-0 outline-none p-4 pl-12 font-medium text-white bg-white/[0.05] transition-colors duration-200 placeholder:text-slate-500"
        />
      </div>

      <QuickExamples
        examples={['MAHARAGAMA', 'maha ragama', 'amba Langoda', '  colombo  ', "kAR ande niya"]}
        onSelect={handleInput}
      />

      {hasResult && (
        <div className="bg-white/[0.04] rounded-xl border border-white/[0.08] p-6">
          <div className="flex items-center justify-between mb-5">
            <h4 className="font-semibold text-white text-lg">
              Normalization Result
            </h4>
            <CopyButton text={normalized} />
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4">
            {/* Before */}
            <div className="flex-1 w-full bg-red-500/10 rounded-xl p-5 border border-red-500/20 text-center">
              <p className="text-xs text-red-400 font-medium uppercase tracking-wider mb-2">
                Original Input
              </p>
              <p className="font-mono text-lg font-semibold text-red-400 break-all">
                "{input}"
              </p>
            </div>

            {/* Arrow */}
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center animate-pulse-slow">
                <svg
                  className="w-5 h-5 text-primary-400 sm:rotate-0 rotate-90"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </div>
            </div>

            {/* After */}
            <div className="flex-1 w-full bg-accent-500/10 rounded-xl p-5 border border-accent-500/20 text-center">
              <p className="text-xs text-accent-400 font-medium uppercase tracking-wider mb-2">
                Normalized Output
              </p>
              <p className="font-mono text-lg font-semibold text-accent-400 break-all">
                "{normalized}"
              </p>
            </div>
          </div>

          {input !== normalized && (
            <div className="mt-4 pt-4 border-t border-white/[0.08]">
              <div className="flex flex-wrap items-center gap-2 text-sm text-slate-400">
                <span className="font-medium">Changes detected:</span>
                {input.trim() !== input && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
                    Trimmed whitespace
                  </span>
                )}
                {input !== input.toLowerCase() && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-blue-500/10 text-blue-400 border border-blue-500/20">
                    Case adjusted
                  </span>
                )}
                {input.includes('  ') && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-purple-500/10 text-purple-400 border border-purple-500/20">
                    Extra spaces removed
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Tab F: City Lookup ───────────────────────── */
function CityLookup() {
  const [city, setCity] = useState('');
  const [info, setInfo] = useState([]);
  const [postals, setPostals] = useState([]);
  const [searched, setSearched] = useState(false);

  const doLookup = useCallback((val) => {
    setCity(val);
    if (!val.trim()) {
      setInfo([]);
      setPostals([]);
      setSearched(false);
      return;
    }
    setSearched(true);
    try {
      const infoRes = sl.getInfoByCity(val.trim());
      setInfo(infoRes || []);
    } catch {
      setInfo([]);
    }
    try {
      const postalsRes = sl.getPostalsByCity(val.trim());
      setPostals(postalsRes || []);
    } catch {
      setPostals([]);
    }
  }, []);

  return (
    <div className="space-y-5">
      <div className="relative">
        <FiHome className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5" />
        <input
          type="text"
          value={city}
          onChange={(e) => doLookup(e.target.value)}
          placeholder="Enter city name e.g. Galle"
          className="w-full rounded-xl border border-white/[0.1] focus:border-primary-500 focus:ring-0 outline-none p-4 pl-12 font-medium text-white bg-white/[0.05] transition-colors duration-200 placeholder:text-slate-500"
        />
      </div>

      <QuickExamples
        examples={['Galle', 'Kandy', 'Colombo', 'Jaffna', 'Matara']}
        onSelect={doLookup}
      />

      {searched && info.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-semibold text-white text-sm uppercase tracking-wider text-slate-400">
            {info.length} record{info.length > 1 ? 's' : ''} found
          </h4>
          {info.map((r, i) => (
            <div
              key={`${r.postalCode}-${i}`}
              className="bg-white/[0.04] rounded-xl border border-white/[0.08] p-5"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold text-white">{r.city}</span>
                <CopyButton text={`${r.city}, ${r.district}, ${r.province} - ${r.postalCode}`} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white/[0.04] rounded-lg p-3">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">District</p>
                  <p className="text-sm font-medium text-white">{r.district}</p>
                </div>
                <div className="bg-white/[0.04] rounded-lg p-3">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Province</p>
                  <p className="text-sm font-medium text-white">{r.province}</p>
                </div>
                <div className="bg-white/[0.04] rounded-lg p-3">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Postal</p>
                  <p className="text-sm font-medium text-primary-400 font-mono">{r.postalCode}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {searched && info.length === 0 && (
        <div className="bg-white/[0.04] rounded-xl border border-white/[0.08] p-6 text-center">
          <div className="text-slate-500 mb-2">
            <FiHome className="w-10 h-10 mx-auto" />
          </div>
          <p className="text-slate-400 font-medium">No city found</p>
          <p className="text-slate-500 text-sm mt-1">Try an exact city name</p>
        </div>
      )}

      {searched && postals.length > 0 && (
        <div className="bg-white/[0.04] rounded-xl border border-white/[0.08] p-5">
          <h4 className="font-semibold text-white mb-3">
            All postal codes for "{city}"
          </h4>
          <div className="flex flex-wrap gap-2">
            {postals.map((r, i) => (
              <span
                key={`${r.postalCode}-${i}`}
                className="inline-block px-3 py-1.5 text-xs font-mono font-medium rounded-lg bg-primary-500/10 text-primary-400 border border-primary-500/20"
              >
                {r.postalCode}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────── Tab G: Postals by District ────────────────────── */
function PostalsByDistrict() {
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [postals, setPostals] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');

  useEffect(() => {
    try {
      setProvinces(sl.getProvinces() || []);
    } catch {
      setProvinces([]);
    }
  }, []);

  const handleProvinceChange = useCallback((province) => {
    setSelectedProvince(province);
    setSelectedDistrict('');
    setPostals([]);
    if (!province) { setDistricts([]); return; }
    try {
      setDistricts(sl.getDistrictsByProvince(province) || []);
    } catch {
      setDistricts([]);
    }
  }, []);

  const handleDistrictChange = useCallback((district) => {
    setSelectedDistrict(district);
    if (!district) { setPostals([]); return; }
    try {
      setPostals(sl.getPostalsByDistrict(district) || []);
    } catch {
      setPostals([]);
    }
  }, []);

  return (
    <div className="space-y-5">
      <div className="relative">
        <FiMap className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5 pointer-events-none" />
        <FiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5 pointer-events-none" />
        <select
          value={selectedProvince}
          onChange={(e) => handleProvinceChange(e.target.value)}
          className="w-full rounded-xl border border-white/[0.1] focus:border-primary-500 focus:ring-0 outline-none p-4 pl-12 pr-10 font-medium text-white appearance-none bg-white/[0.05] transition-colors duration-200 cursor-pointer"
        >
          <option value="" className="bg-slate-800">Select Province</option>
          {provinces.map((p) => (
            <option key={p} value={p} className="bg-slate-800">{p}</option>
          ))}
        </select>
      </div>

      {districts.length > 0 && (
        <div className="relative">
          <FiMapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5 pointer-events-none" />
          <FiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5 pointer-events-none" />
          <select
            value={selectedDistrict}
            onChange={(e) => handleDistrictChange(e.target.value)}
            className="w-full rounded-xl border border-white/[0.1] focus:border-primary-500 focus:ring-0 outline-none p-4 pl-12 pr-10 font-medium text-white appearance-none bg-white/[0.05] transition-colors duration-200 cursor-pointer"
          >
            <option value="" className="bg-slate-800">Select District</option>
            {districts.map((d) => (
              <option key={d} value={d} className="bg-slate-800">{d}</option>
            ))}
          </select>
        </div>
      )}

      {postals.length > 0 && (
        <div className="bg-white/[0.04] rounded-xl border border-white/[0.08] p-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-white">
              Postal codes in {selectedDistrict}
            </h4>
            <span className="text-xs font-medium text-slate-400 bg-white/10 px-2.5 py-1 rounded-full">
              {postals.length} entries
            </span>
          </div>
          <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
            {postals.map((r, i) => (
              <div
                key={`${r.postalCode}-${i}`}
                className="flex items-center justify-between px-4 py-3 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] transition-colors duration-150"
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm font-semibold text-primary-400">{r.postalCode}</span>
                  <span className="text-slate-300 text-sm">{r.city}</span>
                </div>
                <CopyButton text={r.postalCode} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────── Main InteractiveDemo ─────────────────────── */
function InteractiveDemo() {
  const [activeTab, setActiveTab] = useState('postal');
  const [toast, setToast] = useState({ visible: false, message: '' });

  // eslint-disable-next-line no-unused-vars
  const showToast = useCallback((message) => {
    setToast({ visible: true, message });
    setTimeout(() => setToast({ visible: false, message: '' }), 2000);
  }, []);

  const renderTab = () => {
    switch (activeTab) {
      case 'postal':
        return <PostalLookup />;
      case 'search':
        return <FuzzySearch />;
      case 'autocomplete':
        return <AutoComplete />;
      case 'explorer':
        return <Explorer />;
      case 'normalize':
        return <Normalize />;
      case 'citylookup':
        return <CityLookup />;
      case 'postals':
        return <PostalsByDistrict />;
      default:
        return null;
    }
  };

  return (
    <section id="demo" className="py-20 sm:py-28">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section heading */}
        <div className="section-intro text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-4">
            <span className="gradient-text">Try It Live</span>
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Explore the full power of sl-address right in your browser with real
            data.
          </p>
        </div>

        {/* Tab bar */}
        <div className="demo-tabs flex flex-wrap justify-center gap-2 mb-8">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                activeTab === id
                  ? 'bg-primary-600 text-white shadow-lg shadow-primary-600/25'
                  : 'bg-white/[0.06] text-slate-400 hover:bg-white/10 hover:text-slate-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{label}</span>
              <span className="sm:hidden">
                {label.split(' ')[0]}
              </span>
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="demo-content bg-white/[0.03] rounded-2xl p-5 sm:p-8 border border-white/[0.08]">
          {renderTab()}
        </div>
      </div>

      <Toast visible={toast.visible} message={toast.message} />
    </section>
  );
}

export default InteractiveDemo;
